import { supabase } from "./supabase";
import { isNativeShell } from "./native/nativeShell";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export type PushSupport =
  | { supported: true; transport: "webpush" | "apns" }
  | { supported: false; reason: string };

export function checkPushSupport(): PushSupport {
  if (typeof window === "undefined") return { supported: false, reason: "No window" };
  // The native shell delivers through APNs; the web Push APIs do not apply.
  if (isNativeShell()) return { supported: true, transport: "apns" };
  if (!("serviceWorker" in navigator)) return { supported: false, reason: "No service worker" };
  if (!("PushManager" in window)) return { supported: false, reason: "No Push API" };
  if (!("Notification" in window)) return { supported: false, reason: "No Notification API" };
  // iOS Safari only allows Web Push after installing the PWA to the Home Screen,
  // which runs in standalone display mode. In a regular Safari tab, PushManager
  // exists but subscription will fail — surface a clearer message.
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches
    || (navigator as { standalone?: boolean }).standalone === true;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (isIOS && !standalone) {
    return { supported: false, reason: "iOS requires Add to Home Screen" };
  }
  return { supported: true, transport: "webpush" };
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function arrayBufferToBase64Url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.getSubscription();
}

export async function subscribePush(): Promise<PushSubscription> {
  if (!VAPID_PUBLIC_KEY) {
    throw new Error("VITE_VAPID_PUBLIC_KEY is not configured");
  }
  const support = checkPushSupport();
  if (!support.supported) throw new Error(support.reason);

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission denied");
  }

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const subscription = existing ?? (await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
  }));

  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh
    ?? arrayBufferToBase64Url(subscription.getKey("p256dh")!);
  const auth = json.keys?.auth
    ?? arrayBufferToBase64Url(subscription.getKey("auth")!);

  const { error } = await supabase.functions.invoke("subscribe-push", {
    body: {
      endpoint: subscription.endpoint,
      keys: { p256dh, auth },
      userAgent: navigator.userAgent,
    },
  });
  if (error) {
    // Roll back so the user can retry cleanly.
    await subscription.unsubscribe().catch(() => {});
    throw error;
  }

  return subscription;
}

export async function unsubscribePush(): Promise<void> {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  await supabase.functions.invoke("unsubscribe-push", { body: { endpoint } });
}
